import React, { useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Link2, Sparkles, Loader2, ArrowRight, ImageIcon, Zap, CheckCircle, Bot, Download
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORIES = [
  { id: 'elektronika', name: 'Elektronika' },
  { id: 'moda', name: 'Moda' },
  { id: 'dom', name: 'Dom i ogród' },
  { id: 'sport', name: 'Sport' },
  { id: 'jedzenie', name: 'Jedzenie' },
  { id: 'rozrywka', name: 'Rozrywka' },
  { id: 'podroze', name: 'Podróże' },
  { id: 'zdrowie', name: 'Zdrowie' },
  { id: 'motoryzacja', name: 'Motoryzacja' },
  { id: 'inne', name: 'Inne' },
];

export default function DealSubmitPage() {
  const { authHeaders } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [dealPrice, setDealPrice] = useState('');
  const [category, setCategory] = useState('inne');
  const [imageUrl, setImageUrl] = useState('');
  const [imageLocalPath, setImageLocalPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFetching, setAiFetching] = useState(false);
  const [aiFetched, setAiFetched] = useState(false);

  const handleAiFetch = async () => {
    if (!url) { toast.error('Wklej link do produktu'); return; }
    setAiFetching(true);
    setAiFetched(false);
    try {
      const formData = new FormData();
      formData.append('url', url);
      const res = await axios.post(`${API}/ai/fetch-deal-from-url`, formData, { headers: authHeaders() });
      const data = res.data;

      if (data.title) setTitle(data.title);
      if (data.description) setDescription(data.description);
      if (data.category) setCategory(data.category);
      if (data.price) setDealPrice(String(data.price));
      if (data.original_price) setOriginalPrice(String(data.original_price));
      if (data.image_url) setImageUrl(data.image_url);
      if (data.image_local_path) setImageLocalPath(data.image_local_path);

      setAiFetched(true);
      const features = [];
      if (data.title) features.push('tytuł');
      if (data.description) features.push('opis');
      if (data.price) features.push('cenę');
      if (data.image_local_path) features.push('zdjęcie (800x600, zapisane na serwerze)');
      else if (data.image_url) features.push('zdjęcie');
      if (data.category !== 'inne') features.push('kategorię');

      toast.success(`AI pobrał: ${features.join(', ')}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI nie zdołał pobrać danych');
    }
    setAiFetching(false);
  };

  const improveWithGrok = async () => {
    if (!title && !description) { toast.error('Podaj tytuł lub opis do poprawienia'); return; }
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/improve-description`, {
        title, description, category
      }, { headers: authHeaders() });
      setDescription(res.data.improved_description);
      toast.success('Opis poprawiony przez Grok AI!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd AI');
    }
    setAiLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title) { toast.error('Tytuł jest wymagany'); return; }
    setLoading(true);
    try {
      const data = {
        title,
        description,
        url: url || null,
        original_price: originalPrice ? parseFloat(originalPrice) : null,
        deal_price: dealPrice ? parseFloat(dealPrice) : null,
        category,
        image_url: imageUrl || null,
        image_local_path: imageLocalPath || null,
      };
      await axios.post(`${API}/deals`, data, { headers: authHeaders() });
      toast.success('Okazja dodana! +10 Zorków');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd dodawania');
    }
    setLoading(false);
  };

  const previewImage = imageLocalPath
    ? `${process.env.REACT_APP_BACKEND_URL}${imageLocalPath}`
    : imageUrl;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-20 md:pb-6" data-testid="deal-submit-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-6">
        DODAJ OKAZJĘ
      </h1>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* URL + AI Auto-Fetch */}
        <div className="bg-card border border-primary/30 p-4" data-testid="url-section">
          <div className="flex items-center gap-2 mb-3">
            <Bot className="w-5 h-5 text-primary" />
            <Label className="text-sm text-white font-heading font-bold uppercase tracking-wider">
              Wklej link - AI pobierze wszystko!
            </Label>
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            AI automatycznie pobierze tytuł, opis, cenę, kategorię i zdjęcie z linku. Zdjęcie zostanie zoptymalizowane (800x600) i zapisane na naszym serwerze.
          </p>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="deal-url"
                value={url}
                onChange={(e) => { setUrl(e.target.value); setAiFetched(false); }}
                placeholder="https://sklep.pl/produkt..."
                className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
              />
            </div>
            <Button
              type="button"
              data-testid="ai-fetch-btn"
              onClick={handleAiFetch}
              disabled={aiFetching || !url}
              className="bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none px-5"
            >
              {aiFetching ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> AI skanuje...</>
              ) : aiFetched ? (
                <><CheckCircle className="w-4 h-4 mr-1.5" /> Pobrano!</>
              ) : (
                <><Zap className="w-4 h-4 mr-1.5" /> Pobierz AI</>
              )}
            </Button>
          </div>

          {/* AI Fetch progress */}
          <AnimatePresence>
            {aiFetching && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 bg-primary/5 border border-primary/20 p-3"
              >
                <div className="flex items-center gap-2 text-xs text-primary">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="font-heading uppercase tracking-wider">AI analizuje stronę produktu...</span>
                </div>
                <div className="mt-2 space-y-1">
                  {[
                    'Pobieranie strony...',
                    'Analiza treści przez Grok AI...',
                    'Pobieranie i optymalizacja zdjęcia (800x600)...',
                    'Zapis na serwerze...'
                  ].map((step, i) => (
                    <div key={i} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <div className={`w-1.5 h-1.5 rounded-full ${i <= 1 ? 'bg-primary animate-pulse' : 'bg-border'}`} />
                      {step}
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Success summary */}
          <AnimatePresence>
            {aiFetched && !aiFetching && (
              <motion.div
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-3 bg-green-500/10 border border-green-500/30 p-3"
              >
                <div className="flex items-center gap-2 text-xs text-green-400">
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span className="font-heading uppercase tracking-wider">AI pobrał dane z linku!</span>
                </div>
                {imageLocalPath && (
                  <div className="flex items-center gap-2 text-[11px] text-green-400/70 mt-1">
                    <Download className="w-3 h-3" />
                    Zdjęcie zoptymalizowane (800x600) i zapisane na serwerze
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Title */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Tytuł okazji *</Label>
          <Input
            data-testid="deal-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Np. Słuchawki Sony XM5 za pół ceny!"
            className="bg-input border-transparent focus:border-primary rounded-none"
          />
        </div>

        {/* Description + Grok */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-sm text-muted-foreground">Opis</Label>
            <Button
              type="button"
              data-testid="grok-improve-btn"
              onClick={improveWithGrok}
              disabled={aiLoading}
              className="h-7 text-xs bg-primary/20 text-primary hover:bg-primary/30 rounded-none border border-primary/30"
            >
              {aiLoading ? (
                <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Generowanie...</>
              ) : (
                <><Sparkles className="w-3 h-3 mr-1" /> Popraw opis Grok</>
              )}
            </Button>
          </div>
          <Textarea
            data-testid="deal-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Opisz okazję... Grok AI pomoże Ci poprawić opis!"
            rows={5}
            className="bg-input border-transparent focus:border-primary rounded-none resize-none"
          />
        </div>

        {/* Prices */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Cena regularna (zł)</Label>
            <Input
              data-testid="deal-original-price"
              type="number"
              step="0.01"
              value={originalPrice}
              onChange={(e) => setOriginalPrice(e.target.value)}
              placeholder="199.99"
              className="bg-input border-transparent focus:border-primary rounded-none"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground mb-1.5 block">Cena okazji (zł)</Label>
            <Input
              data-testid="deal-price"
              type="number"
              step="0.01"
              value={dealPrice}
              onChange={(e) => setDealPrice(e.target.value)}
              placeholder="99.99"
              className="bg-input border-transparent focus:border-primary rounded-none"
            />
          </div>
        </div>

        {/* Category */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">Kategoria</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="deal-category" className="bg-input border-transparent focus:border-primary rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Image Preview */}
        <div>
          <Label className="text-sm text-muted-foreground mb-1.5 block">
            Zdjęcie
            {imageLocalPath && (
              <span className="text-green-400 ml-2 text-xs font-normal">
                Zapisane na serwerze (800x600)
              </span>
            )}
          </Label>

          {!imageLocalPath && (
            <div className="relative">
              <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="deal-image-url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="URL zdjęcia (lub wklej link produktu - AI pobierze)"
                className="pl-10 bg-input border-transparent focus:border-primary rounded-none"
              />
            </div>
          )}

          {previewImage && (
            <div className="mt-2 relative overflow-hidden bg-secondary border border-border/40" data-testid="image-preview">
              <img
                src={previewImage}
                alt="Preview"
                className="w-full h-48 object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {imageLocalPath && (
                <div className="absolute top-2 right-2 bg-green-600 text-white text-[10px] px-2 py-0.5 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Serwer
                </div>
              )}
            </div>
          )}
        </div>

        {/* Submit */}
        <Button
          data-testid="submit-deal-btn"
          type="submit"
          disabled={loading}
          className="w-full bg-primary text-primary-foreground font-heading font-bold uppercase tracking-wider rounded-none h-12 text-base"
        >
          {loading ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Dodawanie...</>
          ) : (
            <>Dodaj okazję <ArrowRight className="ml-2 w-5 h-5" /></>
          )}
        </Button>
        <p className="text-xs text-center text-muted-foreground">
          Za dodanie okazji otrzymasz <span className="text-zorki font-bold">+10 Zorków</span>
        </p>
      </form>
    </div>
  );
}
